import useSWR from 'swr';
import { apiRequest } from '../lib/api';
import catalog from '../../data/ministries.json';
import type { PressMinistry, PressRelease } from '../types/pressRelease';

const ownPressSlugs = new Set(
  catalog.ministries.filter((m) => m.pressUrl).map((m) => m.slug)
);

const fallbackMinistries: PressMinistry[] = catalog.ministries
  .filter((m) => ownPressSlugs.has(m.slug))
  .map((m) => ({
    slug: m.slug,
    name: m.name,
    shortName: m.shortName,
    releaseCount: 0,
  }));

export function usePressMinistries(): {
  ministries: PressMinistry[];
  loading: boolean;
} {
  const { data, isLoading } = useSWR<PressMinistry[]>(
    '/api/press-releases/ministries',
    apiRequest,
    { fallbackData: fallbackMinistries, shouldRetryOnError: false }
  );
  const ministries = (data ?? fallbackMinistries).filter((m) =>
    ownPressSlugs.has(m.slug)
  );
  return { ministries, loading: isLoading };
}

function fetchReleases(slug: string): Promise<PressRelease[]> {
  return apiRequest<PressRelease[]>(
    `/api/press-releases?ministry=${encodeURIComponent(slug)}`
  );
}

export function usePressReleases(slug: string): {
  releases: PressRelease[];
  loading: boolean;
  error: string | null;
} {
  const { data, error, isLoading } = useSWR<PressRelease[]>(
    slug ? ['press-releases', slug] : null,
    ([, s]) => fetchReleases(s as string)
  );
  return {
    releases: data ?? [],
    loading: isLoading,
    error: error ? (error instanceof Error ? error.message : String(error)) : null,
  };
}
